import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ClientSessionKeyManager } from "@/helpers/ClientSessionKeyManager";
import store from "@/store";

/**
 * Axios interceptor for hybrid encryption approach
 * Frontend sends KMS session key and AES key, backend uses AES key for response encryption
 */
export const applyEncryptionInterceptor = (axiosInstance: AxiosInstance) => {
  const sessionManager = ClientSessionKeyManager.getInstance();
  const isHidePhoneNumbers =
    store.getters["CSPConfiguration/getHidePhoneNumbers"] ?? false;
  const encryptionEnabled =
    process.env.VUE_APP_ENABLE_ENCRYPTION === "true" && isHidePhoneNumbers;

  if (!encryptionEnabled) {
    console.log("Response encryption disabled");
    return;
  }

  // Request interceptor - add both KMS and AES keys to headers
  axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      try {
        // Check if request is for Kokatto Portal
        const isKokattoPortalRequest = config.url?.startsWith(
          process.env.VUE_APP_BASEURL_KOKATTO_PORTAL + "/csplatform" || ""
        );

        if (!isKokattoPortalRequest) {
          return config;
        }

        // Get encryption keys - should be initialized during login
        const { sessionKey, dataKey } = sessionManager.getKeys();

        // Only add encryption headers if we have valid keys
        if (sessionKey && dataKey) {
          config.headers = {
            ...config.headers,
            "x-session-key": sessionKey,
            "x-data-key": dataKey,
          };
          console.log("Encryption keys added to request headers");
        } else {
          console.log("Skipping encryption - no valid keys available");
        }

        return config;
      } catch (error) {
        console.error("Hybrid encryption request interceptor error:", error);
        return config; // Continue without encryption on error
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - decrypt encrypted responses with AES key
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      try {
        // Check if response is encrypted
        const isEncrypted = response.data?.encrypted === true;

        if (isEncrypted) {
          console.log(
            "Encrypted response detected, decrypting with data key..."
          );

          // Extract encrypted data and IV
          const encryptedData = response.data.encryptedData;
          const iv = response.data.iv;

          if (!encryptedData || !iv) {
            console.error("Invalid encrypted response format");
            return response;
          }

          // Decrypt response with frontend data key
          const decryptedData = sessionManager.decryptResponse(
            encryptedData,
            iv
          );

          // Replace response data with decrypted data
          // Maintain original response structure
          response.data = {
            ...response.data,
            ...decryptedData,
            encrypted: false, // Mark as decrypted
          };

          console.log("Response decrypted successfully with data key");
        }

        return response;
      } catch (error) {
        console.error("Hybrid decryption response interceptor error:", error);
        // Return original response on decryption error
        return response;
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  console.log("Hybrid encryption interceptor registered");
};
